import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../web/components-v2/Theme/theme';

/**
 * Styles for the triple-panel support desk. Mirrors the Claude Design "Support Inbox" prototype,
 * mapped onto `THEME` tokens so the desk inherits the product palette/spacing/typography rather than
 * hardcoded hex. Details is the default right-panel tab; the collapsed views rail lives in the list
 * header (back-arrow + switcher).
 */

// The prototype's `--lv-shadow-lg` — used for the larger Search criteria panel. Not a palette
// token, so it's a literal here rather than forced through THEME.colors.
const SHADOW_LG = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';

// Row hover wash — the prototype uses a faint cool gray; background.main is the closest token.
const ROW_HOVER = THEME.colors.background.main;

export const inboxStyles = StyleSheet.create({
	// ---- Root grid ----
	desk: {
		height: '100%',
		overflow: 'hidden',
		minHeight: 0,
		// The support surface is all Sora (per Sean, diverging from the prototype's Lexend body
		// copy); components-v2 controls keep their own font defaults.
		fontFamily: THEME.typography.fontFamilies.sora,
		backgroundColor: THEME.colors.background.main,
	},
	deskColumn: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		minHeight: 0,
	},
	deskBody: {
		flex: 1,
		minHeight: 0,
	},
	notAgentNotice: {
		flex: 'none',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		borderBottom: `1px solid ${THEME.colors.warning.main}`,
		backgroundColor: THEME.colors.warning.light,
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
	},
	paneGrid: {
		display: 'grid',
		height: '100%',
		minHeight: 0,
		overflow: 'hidden',
	},
	viewItemLabel: {
		flex: 1,
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	viewCount: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},

	// ---- Pane 2: ticket list ----
	listPane: {
		minWidth: 0,
		minHeight: 0,
		display: 'flex',
		flexDirection: 'column',
		borderRight: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.light,
	},

	// ---- Search bar + Search criteria popover, expanded from the list-header search icon ----
	searchBar: {
		flex: 'none',
		position: 'relative',
		zIndex: 30,
		padding: '10px 12px',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	searchRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	searchInputWrap: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		flex: 1,
		minWidth: 0,
	},
	searchIcon: {
		position: 'absolute',
		left: 10,
		color: THEME.colors.text.light,
		pointerEvents: 'none',
	},
	searchInput: {
		width: '100%',
		height: 34,
		paddingLeft: 32,
		fontSize: THEME.typography.fontSizes.sm,
	},
	searchInputClearable: {
		paddingRight: 30,
	},
	searchClearBtn: {
		position: 'absolute',
		right: 4,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 26,
		height: 26,
		padding: 0,
		color: THEME.colors.text.light,
		':hover': { color: THEME.colors.text.main },
	},
	filterToggle: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 34,
		height: 34,
		flex: 'none',
		padding: 0,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		background: 'transparent',
		color: THEME.colors.text.light,
		cursor: 'pointer',
		':has(*) > svg': { width: 17, height: 17 },
		':hover': { borderColor: THEME.colors.hover.main, color: THEME.colors.text.main },
	},
	filterToggleActive: {
		borderColor: THEME.colors.primary.main,
		color: THEME.colors.primary.main,
		backgroundColor: THEME.colors.primary.light,
	},
	filterBadge: {
		position: 'absolute',
		top: -6,
		right: -6,
		minWidth: 16,
		height: 16,
		padding: '0 3px',
		boxSizing: 'border-box',
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.primary.main,
		border: `2px solid ${THEME.colors.background.light}`,
		color: THEME.colors.primary.contrastText,
		fontSize: 9,
		fontWeight: 700,
		lineHeight: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	filterOverlay: {
		position: 'fixed',
		inset: 0,
		zIndex: 40,
	},
	filterPanel: {
		position: 'absolute',
		left: 12,
		right: 12,
		top: 'calc(100% - 8px)',
		zIndex: 50,
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: 12,
		boxShadow: SHADOW_LG,
		overflow: 'hidden',
	},
	filterPanelHeader: {
		flex: 'none',
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: '12px 14px',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	filterPanelTitle: {
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	filterPanelClose: {
		marginLeft: 'auto',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 24,
		height: 24,
		padding: 0,
		border: 'none',
		borderRadius: 6,
		background: 'transparent',
		color: THEME.colors.text.light,
		cursor: 'pointer',
		':has(*) > svg': { width: 15, height: 15 },
		':hover': { backgroundColor: ROW_HOVER, color: THEME.colors.text.main },
	},
	filterPanelBody: {
		maxHeight: '56vh',
		overflowY: 'auto',
		padding: '12px 14px',
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	filterRow: {
		display: 'flex',
		flexDirection: 'column',
	},
	filterRowTrigger: {
		width: '100%',
		height: 36,
		justifyContent: 'flex-start',
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.standard,
	},
	filterRowLabel: {
		color: THEME.colors.text.light,
	},
	filterRowValue: {
		marginLeft: 'auto',
		color: THEME.colors.text.light,
	},
	filterRowValueActive: {
		color: THEME.colors.text.main,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	filterCsmLabel: {
		marginBottom: THEME.spacing.xs,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	filterCsmInput: {
		height: 36,
		fontSize: THEME.typography.fontSizes.sm,
	},
	filterCheckRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		marginTop: THEME.spacing.xs,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		cursor: 'pointer',
	},
	filterPanelFooter: {
		flex: 'none',
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: '12px 14px',
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},
	filterClearBtn: {
		height: 'auto',
		padding: 0,
		border: 'none',
		background: 'transparent',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		cursor: 'pointer',
		':hover': { color: THEME.colors.text.main },
	},
	filterDoneBtn: {
		marginLeft: 'auto',
		height: 'auto',
		padding: '7px 18px',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	listHeader: {
		padding: '14px 12px 10px',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		flex: 'none',
	},
	listHeaderRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 10,
		position: 'relative',
		minWidth: 0,
	},
	listHeaderLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		minWidth: 0,
		flex: '1 1 auto',
	},
	backBtn: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 24,
		height: 28,
		marginLeft: 0,
		border: 'none',
		borderRadius: THEME.common.borderRadius,
		background: 'transparent',
		cursor: 'pointer',
		color: THEME.colors.text.main,
		flex: 'none',
		':has(*) > svg': { width: 'auto', height: 'auto' },
		':hover': { backgroundColor: ROW_HOVER },
	},
	listTitleBtn: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		gap: 0,
		height: 'auto',
		background: 'transparent',
		border: 'none',
		padding: 0,
		cursor: 'pointer',
		minWidth: 0,
	},
	listTitleGroup: {
		display: 'flex',
		alignItems: 'center',
		gap: 5,
		minWidth: 0,
		flex: '0 1 auto',
		overflow: 'hidden',
	},
	listTitle: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		whiteSpace: 'nowrap',
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	listSubCount: {
		fontSize: 13,
		color: THEME.colors.text.light,
		flex: 'none',
		whiteSpace: 'nowrap',
	},
	searchToggleBtn: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 28,
		height: 28,
		padding: 0,
		border: 'none',
		borderRadius: THEME.common.borderRadius,
		background: 'transparent',
		cursor: 'pointer',
		color: THEME.colors.text.light,
		flex: 'none',
		':has(*) > svg': { width: 16, height: 16 },
		':hover': { backgroundColor: ROW_HOVER, color: THEME.colors.text.main },
	},
	searchToggleBtnActive: {
		backgroundColor: THEME.colors.primary.light,
		color: THEME.colors.primary.main,
	},
	switcherChevron: {
		flex: 'none',
		alignSelf: 'center',
		color: THEME.colors.text.main,
	},
	// Quick-jump view menu, rendered through the components-v2 Dropdown (base-ui Menu handles
	// positioning/dismissal), so this only sets the popup width.
	viewMenu: {
		width: 240,
	},
	viewMenuItem: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		gap: 10,
		width: '100%',
		height: 'auto',
		padding: '8px 10px',
		border: 'none',
		borderRadius: 7,
		background: 'transparent',
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		textAlign: 'left',
		':hover': { backgroundColor: ROW_HOVER },
	},
	viewMenuItemActive: {
		backgroundColor: THEME.colors.primary.light,
	},
	listScroll: {
		flex: 1,
		overflowY: 'auto',
		minHeight: 0,
	},
	paginationBar: {
		flex: 'none',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: THEME.spacing.xs,
		padding: '9px 10px',
		borderTop: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.light,
	},
	paginationPages: {
		display: 'flex',
		alignItems: 'center',
		gap: 3,
		minWidth: 0,
		overflow: 'hidden',
	},
	paginationArrow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 28,
		height: 28,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		background: 'transparent',
		color: THEME.colors.text.main,
		cursor: 'pointer',
		flex: 'none',
		':has(*) > svg': { width: 16, height: 16 },
		':hover': { backgroundColor: ROW_HOVER },
		':disabled': {
			cursor: 'default',
			color: THEME.colors.icon.disabled,
			backgroundColor: 'transparent',
		},
	},
	paginationPage: {
		minWidth: 28,
		height: 28,
		padding: '0 7px',
		border: 'none',
		borderRadius: THEME.common.borderRadius,
		background: 'transparent',
		color: THEME.colors.text.light,
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		':hover': {
			backgroundColor: ROW_HOVER,
			color: THEME.colors.text.main,
		},
		':disabled': {
			cursor: 'default',
		},
	},
	paginationPageActive: {
		backgroundColor: THEME.colors.primary.light,
		color: THEME.colors.primary.main,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	ticketRow: {
		display: 'flex',
		alignItems: 'stretch',
		justifyContent: 'flex-start',
		flexDirection: 'column',
		gap: 6,
		width: '100%',
		height: 'auto',
		padding: '10px 16px',
		fontFamily: THEME.typography.fontFamilies.sora,
		borderRadius: 0,
		cursor: 'pointer',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		borderLeft: '3px solid transparent',
		textAlign: 'left',
		':hover': { backgroundColor: ROW_HOVER },
	},
	ticketRowActive: {
		backgroundColor: THEME.colors.primary.light,
		borderLeftColor: THEME.colors.primary.main,
		':hover': { backgroundColor: THEME.colors.primary.light },
	},
	// Pending tickets are waiting on the customer — sunk to the bottom of the page and dimmed.
	ticketRowPending: {
		opacity: 0.6,
	},
	rowTop: {
		display: 'flex',
		alignItems: 'center',
		gap: 9,
	},
	rowAvatar: {
		width: 30,
		height: 30,
		flex: 'none',
	},
	rowName: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		flex: 1,
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	rowTime: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
		flex: 'none',
	},
	rowSubject: {
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	rowPreview: {
		fontSize: 13,
		color: THEME.colors.text.light,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	rowPills: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		flexWrap: 'wrap',
	},

	// ---- Pane 3: conversation ----
	conversationPane: {
		position: 'relative',
		minWidth: 0,
		minHeight: 0,
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: THEME.colors.background.main,
		overflow: 'hidden',
	},

	// ---- Pane 4: drawer (Assistant / Details) ----
	supportDrawer: {
		minWidth: 0,
		minHeight: 0,
		height: '100%',
		overflow: 'hidden',
	},
	supportDrawerContent: {
		borderLeft: `1px solid ${THEME.colors.border.light}`,
		overflowY: 'hidden' as const,
	},
	supportDrawerPlaceholder: {
		width: 456,
		minWidth: 456,
		minHeight: 0,
		display: 'flex',
		borderLeft: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.light,
	},
	rightContent: {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
		padding: 16,
	},
	rightAssistantContent: {
		flex: 1,
		minHeight: 0,
		overflow: 'hidden',
	},
	detailHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: 11,
		paddingBottom: 14,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	detailAvatar: {
		width: 42,
		height: 42,
		flex: 'none',
	},
	detailName: {
		fontSize: 15,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	detailEmail: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.primary.main,
	},
	detailCustomerAction: {
		height: 'auto',
		justifyContent: 'flex-start',
		padding: 0,
		fontFamily: 'inherit',
		fontSize: THEME.typography.fontSizes.xs,
	},
	detailSectionTitle: {
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.04em',
		color: THEME.colors.text.light,
		textTransform: 'uppercase',
		margin: '16px 0 10px',
	},
	detailRows: {
		display: 'flex',
		flexDirection: 'column',
		gap: 9,
	},
	// Created / Messages sit below the editable controls; add separation from them.
	detailRowsStacked: {
		marginTop: 12,
	},
	// Editable Status / Priority / Assignee / Category controls (relocated from the app bar).
	detailEditRows: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	},
	// Inline failure surface for the detail editor mutations (no toast in support).
	detailEditError: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.danger.dark,
	},
	detailEditRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 12,
	},
	detailRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: 12,
	},
	detailKey: {
		fontSize: 13,
		color: THEME.colors.text.light,
	},
	detailValue: {
		fontSize: 13,
		color: THEME.colors.text.main,
		textAlign: 'right',
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	detailLink: {
		fontSize: 13,
		color: THEME.colors.primary.main,
		textDecoration: 'none',
		':hover': { textDecoration: 'underline' },
	},

	// ---- Previous conversations (other tickets from the same contact) ----
	linkedList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 7,
	},
	linkedRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		gap: 9,
		width: '100%',
		height: 'auto',
		minHeight: 0,
		textAlign: 'left',
		padding: '9px 11px',
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: 9,
		background: 'transparent',
		cursor: 'pointer',
		color: THEME.colors.text.main,
		fontFamily: THEME.typography.fontFamilies.sora,
		':hover': { backgroundColor: THEME.colors.hover.light },
	},
	linkedIcon: {
		flex: 'none',
		color: THEME.colors.text.light,
		display: 'flex',
	},
	linkedText: {
		flex: 1,
		minWidth: 0,
		fontSize: 13,
		color: THEME.colors.text.main,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	linkedViewAll: {
		color: THEME.colors.primary.main,
		fontWeight: THEME.typography.fontWeights.semibold,
	},

	// ---- Shared empty / placeholder states ----
	empty: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 10,
		padding: 32,
		textAlign: 'center',
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	loading: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 32,
	},

	// ---- Reports screen (placeholder until the Assistant-impact dashboard lands) ----
	reportsScreen: {
		height: '100%',
		minHeight: 0,
		overflowY: 'auto',
		backgroundColor: THEME.colors.background.main,
		padding: '24px 28px',
	},
	reportsInner: {
		width: '100%',
		maxWidth: 1320,
		margin: '0 auto',
		display: 'flex',
		flexDirection: 'column',
		gap: 18,
	},
	reportsTitle: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.xl,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
		color: THEME.colors.text.main,
	},
	reportsPlaceholderCard: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 8,
		minHeight: 320,
		border: `1px dashed ${THEME.colors.border.main}`,
		borderRadius: 12,
		backgroundColor: THEME.colors.background.light,
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
});
